/**
 * pages/Login.tsx — the one view rendered outside the shell, and the refusal page with it.
 *
 * There is no password field here and there never will be: sign-in is a redirect to the identity provider
 * (`/auth/login`), which sets the PKCE/state/nonce transaction cookie and comes back to `/auth/callback`.
 * A form posting credentials to this application would be a second door into the same building.
 *
 * **There is no email field either**, and that is the change of 6 August 2026. It carried an optional
 * `login_hint` — "a convenience for the user, never a constraint on the answer", as the route itself puts
 * it — and in exchange made an SSO page read as the front of a password form, down to inviting the
 * password manager in with `autoComplete="email"`. `GET /auth/login?email=` still accepts a hint from
 * anything that wants to send one; this page simply no longer asks a human to type it.
 *
 * **The page never navigates on its own, and must not be made to.** It draws a card and waits for a click,
 * as its two sibling applications both do. An effect that assigned `/auth/login` would turn sign-out into
 * an infinite bounce: `App.onSignOut` lands here with no surviving signal that a sign-out happened, the
 * provider still holds its own SSO session, and the callback would hand back a fresh one before the card
 * was ever seen.
 *
 * **Signing in is not admission**, and this page says so. The server creates a first-time signer-in as
 * `pending`, which can do precisely nothing until an administrator admits them; the callback redirects
 * home with `?error=not_admitted`, and rendering that as "sign-in failed" would send someone to reset a
 * password they do not have.
 *
 * **This page is also the refusal page** — there is no separate error route. One `?error=` code selects a
 * variant of this one card from a closed table, and an unrecognised value falls back to a safe sentence
 * rather than a blank. Where the person can act, the variant offers a way back to the provider; where only
 * an administrator can, it withdraws the retry and names who to ask instead.
 *
 * The development sign-in is offered when `/api/me`'s `auth.mode` says the server built the development
 * authenticator, OR when the address bar carries `?dev` — because on a cold browser `/api/me` answers 401
 * and `mode` is therefore always `oidc`, which left the development sign-in undrawable at the one moment
 * it is wanted. The marker publishes nothing: `POST /auth/dev-login` is registered only where the
 * development authenticator was built, so elsewhere it 404s and the form says so. This page adds no lock
 * and removes none.
 */
import { useState } from "react";
import { devSignIn } from "../lib/api";
import { Mark } from "../brand/Mark";
import { Banner, Button, ButtonLink, TextField, onEnter } from "../components/ui";

/** Where sign-in begins. A real navigation: the provider needs the browser, and the cookie is set on this leg. */
const SIGN_IN_HREF = "/auth/login";

/** Home, reached without whatever query string brought us here. */
const HOME_HREF = "/";

export interface LoginProps {
  /** From `/api/me` when it could be read; `oidc` is the safe assumption when it could not. */
  readonly mode: "oidc" | "dev";
  /** The refusal in force: the callback's `?error=` code, or `not_admitted` from a live-but-refused session. */
  readonly refusal?: string | null;
  /** Draw the development sign-in even though `mode` could not be read. Set by `?dev` in the address bar. */
  readonly devOffered?: boolean;
  /** Supplied only where a session exists to re-read — a suspended account, not a refused sign-in. */
  readonly onRecheck?: () => void;
  /** Called once a session exists, so the app can re-read `/api/me` rather than reload the document. */
  readonly onSignedIn: () => void;
}

/** What a variant offers as its way out. `none` is a deliberate dead end, not an omission. */
type Action =
  | { readonly kind: "sso"; readonly label: string }
  | { readonly kind: "recheck"; readonly label: string }
  | { readonly kind: "home"; readonly label: string }
  | { readonly kind: "none" };

interface Variant {
  /** Null in the normal state: the brand lockup is the whole heading when nothing has gone wrong. */
  readonly heading: string | null;
  readonly lede: string;
  readonly action: Action;
  readonly foot: string | null;
}

const NORMAL: Variant = {
  heading: null,
  lede: "Scheduled changelogs, written the same way every time, published to Confluence and Slack.",
  action: { kind: "sso", label: "Sign in with SSO" },
  foot: "Signing in does not grant access. A new account is created pending, and an administrator admits it.",
};

/** The refusal was not one this build knows. Say something true rather than nothing. */
const UNRECOGNISED: Variant = {
  heading: "That sign-in was refused",
  lede: "That sign-in was refused, and this page was not told why. Trying again may work; if it does not, an administrator can say what happened.",
  action: { kind: "sso", label: "Try again" },
  foot: null,
};

/**
 * Every reason code, said plainly. Each is already safe to say out loud — that is by design.
 *
 * The first four are the ones a browser can actually arrive with from the callback. The rest are thrown
 * only behind an authenticated API request and can never reach `?error=`; they are here because the
 * parameter is a query string and anybody may type one, and a code with no sentence is a blank card.
 */
const REFUSALS: Readonly<Record<string, Variant>> = {
  not_admitted: {
    heading: "Awaiting admission",
    lede: "Your account exists but is not active. An administrator has to admit it; nothing you can do here will change that.",
    action: { kind: "recheck", label: "Check again" },
    foot: "Nothing more to do here for now — accounts are admitted on the People screen.",
  },
  domain_not_allowed: {
    heading: "That address cannot sign in",
    lede: "This deployment does not accept sign-ins from your email domain. Sign in with your work account, or ask an administrator whether the domain should be allowed.",
    action: { kind: "sso", label: "Use a different account" },
    foot: null,
  },
  // Deliberately says nothing about WHOSE account it is, and offers no retry: trying again produces
  // exactly this, and only an administrator can resolve it. Reported as a bare 400 until 5 August 2026.
  identity_conflict: {
    heading: "That address is already in use",
    lede: "An account already exists for your email address and is bound to a different sign-in identity. An administrator has to resolve that before you can sign in; trying again will not.",
    action: { kind: "none" },
    foot: "A superuser can release the account from its old identity on the People screen — or, if the address was reissued to somebody new, give you an account of your own.",
  },
  not_authenticated: {
    heading: "Sign-in did not complete",
    lede: "Something interrupted the sign-in between here and your identity provider. Trying again usually works.",
    action: { kind: "sso", label: "Try again" },
    foot: null,
  },
  scope_denied: {
    heading: "That is not permitted",
    lede: "Your account has no access to that business unit. Access is carried by a group, and an administrator grants it.",
    action: { kind: "home", label: "Back" },
    foot: null,
  },
  scope_unknown: {
    heading: "That is not permitted",
    lede: "That business unit does not exist here, or it has been disabled.",
    action: { kind: "home", label: "Back" },
    foot: null,
  },
  resource_denied: {
    heading: "That is not permitted",
    lede: "Your account is not permitted to use that. Permission is carried by a group, and an administrator grants it.",
    action: { kind: "home", label: "Back" },
    foot: null,
  },
  section_denied: {
    heading: "That is not permitted",
    lede: "Your account is not permitted to open that administrative section. Permission is carried by a group, and an administrator grants it.",
    action: { kind: "home", label: "Back" },
    foot: null,
  },
  role_required: {
    heading: "That is not permitted",
    lede: "That needs a role your account does not hold. Roles are carried by a group, and an administrator grants them.",
    action: { kind: "home", label: "Back" },
    foot: null,
  },
};

/** The variant in force. An absent, empty or unrecognised code degrades — never to a blank card. */
export function variantFor(refusal: string | null | undefined): Variant {
  if (refusal === undefined || refusal === null || refusal === "") return NORMAL;
  return REFUSALS[refusal] ?? UNRECOGNISED;
}

export function Login({
  mode,
  refusal,
  devOffered = false,
  onRecheck,
  onSignedIn,
}: LoginProps): JSX.Element {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const variant = variantFor(refusal);
  const showDev = mode === "dev" || devOffered;

  const signInAsDeveloper = async (): Promise<void> => {
    const address = email.trim();
    if (address === "") {
      setError("Enter the email address to sign in as.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await devSignIn(address);
      onSignedIn();
    } catch {
      // Deliberately not `ApiError.userMessage`: `devSignIn` labels every failure `not_authenticated`,
      // whose sentence is "your session has ended" — which is not what happened, and is exactly wrong
      // where the route is absent because this is not a development build.
      setError("The development sign-in did not complete. Is the server running in development mode?");
    } finally {
      setBusy(false);
    }
  };

  /**
   * The one way in, as a real anchor — never a click handler that assigns `location`.
   *
   * `recheck` degrades to the same anchor when there is no session to re-read, because with no session
   * "check again" and "sign in again" are the same act.
   */
  const action = (): JSX.Element | null => {
    switch (variant.action.kind) {
      case "none":
        return null;
      case "home":
        return (
          <ButtonLink href={HOME_HREF} variant="secondary" size="lg" block>
            {variant.action.label}
          </ButtonLink>
        );
      case "recheck":
        return onRecheck === undefined ? (
          <ButtonLink href={SIGN_IN_HREF} variant="secondary" size="lg" block>
            {variant.action.label}
          </ButtonLink>
        ) : (
          <Button variant="secondary" size="lg" block onClick={onRecheck}>
            {variant.action.label}
          </Button>
        );
      case "sso":
      default:
        return (
          <ButtonLink href={SIGN_IN_HREF} variant="primary" size="lg" block>
            {variant.action.label}
          </ButtonLink>
        );
    }
  };

  return (
    <div className="login">
      <main className="login__card">
        {/* The mark AND the name, which the app bar deliberately does not do. §1.3's objection is to
            restating the name where the viewer is already inside the application; this is the one page
            they may have arrived at from a link, so it is the one page that has to introduce itself. */}
        <div className="login__brand">
          <Mark className="login__mark" />
          <h1 className="login__title">Ticketless</h1>
        </div>

        {variant.heading === null ? null : <h2 className="login__heading">{variant.heading}</h2>}
        <p className="login__lede">{variant.lede}</p>

        {action()}

        {variant.foot === null ? null : <p className="login__footnote">{variant.foot}</p>}

        {showDev ? (
          <div className="login__dev">
            <h2 className="login__dev-title">Development sign-in</h2>
            {/* Directly above the region it concerns, per 03-components §6.2. It reports this form's own
                failure and nothing else; a refusal from the provider is the card above, not a banner. */}
            {error !== null ? <Banner tone="danger">{error}</Banner> : null}
            <div className="stack" onKeyDown={onEnter(() => void signInAsDeveloper())}>
              <TextField
                label="Sign in as"
                value={email}
                onChange={setEmail}
                type="email"
                inputMode="email"
                autoComplete="email"
                hint="The address is taken at its word and no provider is contacted. Development builds only."
                required
              />
              <Button
                variant="secondary"
                size="lg"
                block
                loading={busy}
                onClick={() => void signInAsDeveloper()}
              >
                Sign in (development)
              </Button>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
